import { useId, type ReactNode } from "react";
import type { ConnectionMissing, ConnectionPerson, ConnectionState, ConnectionsResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTab } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Reason } from "../../design/components/Reason";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { StatusPill } from "../../design/components/StatusPill";
import type { Tone } from "../../design/components/tone";
import { messageForError } from "../../i18n/errors";
import { formatNumericDateTime } from "../../i18n/format";
import { useT } from "../../i18n/locale";
import type { AppRouter } from "../../router";
import { useAccess } from "../../session/access";
import { connectionsQuery } from "./connections-queries";
import "./connections.css";

// Impostazioni → «Collegamenti» (M7 parte 2): in un posto solo lo stato di ogni collegamento dell'azienda, in parole comuni, con
// l'ultima volta che ha funzionato, che cosa manca e dove si sistema. Sola lettura: nessun controllo cambia niente, ogni riga rimanda
// alla sezione che c'è già (o dice che la sistema Gioia365, o la persona dal suo account). La vede chi il server lascia leggere:
// l'Admin e il Super admin dell'azienda e il Super admin di Gioia365 (`sections.ts`, `minLevel`).

type T = ReturnType<typeof useT>;
type Path = keyof AppRouter["routesByPath"];
type Fix = { kind: "link"; to: Path; label: string } | { kind: "note"; text: string };

const STATE_LOOK: Readonly<Record<ConnectionState, { tone: Tone; icon: "check" | "clock" | "minus" | "alert" }>> = {
  working: { tone: "success", icon: "check" },
  incomplete: { tone: "warning", icon: "clock" },
  off: { tone: "neutral", icon: "minus" },
  error: { tone: "danger", icon: "alert" },
};

export function ConnectionsPage() {
  return (
    <SettingsLayout section="connections">
      <ConnectionsSection />
    </SettingsLayout>
  );
}

function ConnectionsSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const connections = useQuery(connectionsQuery(tab));
  const organization = access.context.organization.name;

  let body: ReactNode;
  if (connections.data) {
    body = <ConnectionsList data={connections.data} isPlatform={access.isPlatform} timeZone={access.context.settings.timeZone} />;
  } else if (connections.isError) {
    body = (
      <div className="g-connections__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, connections.error)}
        </Reason>
        <Button onClick={() => void connections.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else {
    body = <p className="g-connections__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <AboutBox title={t("connections.about.title")}>
        <p>{t("connections.about.text", { organization })}</p>
        <h2>{t("connections.about.whoTitle")}</h2>
        <p>{t("connections.about.who")}</p>
      </AboutBox>
      {body}
    </>
  );
}

function ConnectionsList({ data, isPlatform, timeZone }: { data: ConnectionsResponse; isPlatform: boolean; timeZone: string }) {
  const t = useT();
  const when = (iso: string | null) => (iso === null ? null : formatNumericDateTime(timeZone, iso));
  const sending: Fix = { kind: "link", to: "/impostazioni/invio-dei-whatsapp", label: t("connections.fix.whatsappSending") };
  const intake: Fix = { kind: "link", to: "/impostazioni/ingressi-dei-lead", label: t("connections.fix.leadIntake") };
  const byGioia: Fix = { kind: "note", text: t("connections.fix.byGioia365") };
  const failedArrivals = (count: number) => (count > 0 ? [t("connections.facts.failedArrivals", { count })] : []);

  return (
    <>
      <Group title={t("connections.groups.calls")}>
        <Row
          name={t("connections.items.telephony")}
          state={data.telephony.state}
          lastWorked={when(data.telephony.lastWorkedAt)}
          missing={data.telephony.missing}
          facts={data.telephony.recording === null ? [] : [t(data.telephony.recording ? "connections.facts.recordingOn" : "connections.facts.recordingOff")]}
          fixes={[isPlatform ? { kind: "link", to: "/gioia365/aziende", label: t("connections.fix.platformCompanies") } : byGioia]}
        />
        <Row
          name={t("connections.items.callerNumbers")}
          state={data.callerNumbers.state}
          lastWorked={null}
          missing={data.callerNumbers.missing}
          facts={[
            t("connections.facts.numbers", {
              total: data.callerNumbers.total,
              verified: data.callerNumbers.verified,
              pending: data.callerNumbers.pending,
              failed: data.callerNumbers.failed,
              assigned: data.callerNumbers.assigned,
            }),
          ]}
          people={{ label: t("connections.people.withoutNumber"), people: data.callerNumbers.peopleWithoutNumber }}
          fixes={[{ kind: "link", to: "/impostazioni/team", label: t("connections.fix.team") }]}
        />
      </Group>

      <Group title={t("connections.groups.whatsapp")}>
        <Row
          name={t("connections.items.whatsappOfficial")}
          state={data.whatsappOfficial.state}
          lastWorked={when(data.whatsappOfficial.lastWorkedAt)}
          missing={data.whatsappOfficial.missing}
          facts={
            data.whatsappOfficial.state === "off"
              ? []
              : [
                  t("connections.facts.senders", { ready: data.whatsappOfficial.ready, notReady: data.whatsappOfficial.notReady }),
                  ...(data.whatsappOfficial.templates > 0 ? [t("connections.facts.templates", { approved: data.whatsappOfficial.approvedTemplates, total: data.whatsappOfficial.templates })] : []),
                ]
          }
          fixes={[
            sending,
            ...(data.whatsappOfficial.missing.includes("whatsapp_meta_templates")
              ? [{ kind: "link" as const, to: "/impostazioni/modelli-whatsapp" as const, label: t("connections.fix.whatsappTemplates") }]
              : []),
          ]}
        />
        <Row
          name={t("connections.items.whatsappAgile")}
          state={data.whatsappAgile.state}
          lastWorked={when(data.whatsappAgile.lastWorkedAt)}
          missing={data.whatsappAgile.missing}
          facts={data.whatsappAgile.state === "off" ? [] : [t("connections.facts.senders", { ready: data.whatsappAgile.ready, notReady: data.whatsappAgile.notReady })]}
          fixes={[sending]}
        />
        <Row
          name={t("connections.items.whatsappManual")}
          state={data.whatsappManual.state}
          lastWorked={when(data.whatsappManual.lastWorkedAt)}
          missing={data.whatsappManual.missing}
          facts={[t("connections.facts.manualPeople", { count: data.whatsappManual.people })]}
          people={{ label: t("connections.people.withoutWhatsApp"), people: data.peopleWithoutWhatsApp }}
          fixes={[sending]}
        />
      </Group>

      <Group title={t("connections.groups.intake")}>
        <Row
          name={t("connections.items.intakeMeta")}
          state={data.intakeMeta.state}
          lastWorked={when(data.intakeMeta.lastWorkedAt)}
          missing={data.intakeMeta.missing}
          facts={[...(data.intakeMeta.pageName === null ? [] : [t("connections.facts.page", { page: data.intakeMeta.pageName })]), ...failedArrivals(data.intakeMeta.failedRecently)]}
          fixes={[intake]}
        />
        <Row
          name={t("connections.items.intakeWeb")}
          state={data.intakeWeb.state}
          lastWorked={when(data.intakeWeb.lastWorkedAt)}
          missing={data.intakeWeb.missing}
          facts={failedArrivals(data.intakeWeb.failedRecently)}
          fixes={[intake]}
        />
        <Row
          name={t("connections.items.intakeGhl")}
          state={data.intakeGhl.state}
          lastWorked={when(data.intakeGhl.lastWorkedAt)}
          missing={data.intakeGhl.missing}
          facts={failedArrivals(data.intakeGhl.failedRecently)}
          fixes={[intake]}
        />
      </Group>

      <Group title={t("connections.groups.other")}>
        <Row
          name={t("connections.items.google")}
          state={data.google.state}
          lastWorked={null}
          missing={data.google.missing}
          people={{ label: t("connections.people.googleConnected"), people: data.google.connectedPeople, empty: t("connections.people.nobody") }}
          extraPeople={{ label: t("connections.people.googleFailed"), people: data.google.failedPeople }}
          fixes={[{ kind: "note", text: t("connections.fix.ownAccount") }]}
        />
        <Row
          name={t("connections.items.closeImport")}
          state={data.closeImport.state}
          lastWorked={when(data.closeImport.lastWorkedAt)}
          missing={data.closeImport.missing}
          facts={closeFacts(t, data.closeImport, when)}
          fixes={[data.closeImport.detailed ? { kind: "link", to: "/impostazioni/importazione-ed-esportazione/close", label: t("connections.fix.closeImport") } : byGioia]}
        />
        <Row name={t("connections.items.email")} state={data.email.state} lastWorked={null} missing={data.email.missing} fixes={[byGioia]} />
      </Group>
    </>
  );
}

/** L'importazione da Close: per il Super admin di Gioia365 la prossima e l'ultima della sera; per gli altri soltanto accesa o spenta. */
function closeFacts(t: T, close: ConnectionsResponse["closeImport"], when: (iso: string | null) => string | null): string[] {
  if (!close.detailed) return [t(close.state === "off" ? "connections.facts.closeOff" : "connections.facts.closeOn")];
  const next = when(close.nextAt);
  return [
    ...(next === null ? [] : [t("connections.facts.closeNext", { when: next })]),
    ...(close.lastOutcome === null ? [] : [t("connections.facts.closeLast", { outcome: t(`connections.closeOutcome.${close.lastOutcome}`) })]),
  ];
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  const titleId = useId();
  return (
    <section className="g-connections__group" aria-labelledby={titleId}>
      <h2 id={titleId}>{title}</h2>
      <ul className="g-connections__list">{children}</ul>
    </section>
  );
}

type PeopleLine = { label: string; people: readonly ConnectionPerson[]; empty?: string };

function Row(props: {
  name: string;
  state: ConnectionState;
  lastWorked: string | null;
  missing: readonly ConnectionMissing[];
  facts?: readonly string[];
  people?: PeopleLine;
  extraPeople?: PeopleLine;
  fixes: readonly Fix[];
}) {
  const t = useT();
  const nameId = useId();
  const look = STATE_LOOK[props.state];
  const peopleLines = [props.people, props.extraPeople].filter((line): line is PeopleLine => line !== undefined && (line.people.length > 0 || line.empty !== undefined));
  return (
    <li className="g-connections__item" aria-labelledby={nameId} data-state={props.state}>
      <div className="g-connections__head">
        <h3 id={nameId}>{props.name}</h3>
        <StatusPill tone={look.tone} icon={look.icon}>
          {t(`connections.state.${props.state}`)}
        </StatusPill>
      </div>
      {props.lastWorked === null ? null : <p className="g-connections__muted">{t("connections.lastWorked", { when: props.lastWorked })}</p>}
      {props.facts?.map((fact) => (
        <p key={fact}>{fact}</p>
      ))}
      {props.missing.length > 0 ? (
        <div className="g-connections__missing">
          <p className="g-connections__label">{t("connections.missingTitle")}</p>
          <ul>
            {props.missing.map((code) => (
              <li key={code}>{t(`connections.missing.${code}`)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {peopleLines.map((line) => (
        <p key={line.label}>
          <span className="g-connections__label">{line.label}</span> {line.people.length > 0 ? line.people.map((person) => person.displayName).join(", ") : line.empty}
        </p>
      ))}
      <div className="g-connections__fixes">
        {props.fixes.map((fix) =>
          fix.kind === "link" ? (
            <Link key={fix.to} to={fix.to} className="g-connections__link">
              {fix.label}
            </Link>
          ) : (
            <p key={fix.text} className="g-connections__muted">
              {fix.text}
            </p>
          ),
        )}
      </div>
    </li>
  );
}
